import { DataLayer } from '@piwikpro/react-piwik-pro';
import React from 'react';

import { Link } from 'gatsby';

import {
  ClaimWithContentVideo,
  ImageGroup,
  IntroText,
  TextBlock,
} from '@mewa/ui';

import { ContentSectionContextProps } from '../../fragment-and-types';
import { StrapiButton } from '../button';
import { StrapiImage } from '../image';
import { SapFormData } from '../sap-form';
import { gatsbyVideoDataFromStrapiVideo } from '../video/responsive-video';
import { groupImages } from './group-images';
import {
  buttonComponentName,
  ButtonData,
  ClaimWithContentSection,
  introTextComponentName,
  IntroTextData,
  ResponsiveImage,
  SectionWithImageGroups,
  textBlockComponentName,
  TextBlockData,
  videoWithCaptionComponentName,
  VideoWithCaptionData,
} from './types';

type SectionType = ClaimWithContentSection | SectionWithImageGroups;

const isTextBlock = (component: SectionType): component is TextBlockData =>
  'strapi_component' in component &&
  component.strapi_component === textBlockComponentName;

export const isIntroText = (
  component: SectionType,
): component is IntroTextData =>
  'strapi_component' in component &&
  component.strapi_component === introTextComponentName;

export const isButton = (component: SectionType): component is ButtonData =>
  'strapi_component' in component &&
  component.strapi_component === buttonComponentName;

export const isVideoWithCaption = (
  component: SectionType,
): component is VideoWithCaptionData =>
  'strapi_component' in component &&
  component.strapi_component === videoWithCaptionComponentName;

const isImageGroup = (group: SectionType): group is ResponsiveImage[] =>
  Array.isArray(group);

export const Sections = ({
  content,
  conversion,
  piwikEventNameForHeaderCTAButton,
  sapForm,
  ...context
}: ContentSectionContextProps & {
  content: ClaimWithContentSection[];
  conversion: {
    link: string;
    label: string;
    onClick?: () => void;
  };
  piwikEventNameForHeaderCTAButton?: string;
  sapForm?: SapFormData | undefined;
}) => {
  return groupImages(content).map((section, index) => {
    if (isIntroText(section)) {
      const link = {
        component: Link,
        props: {
          to: conversion.link,
        },
      };

      const onCtaClick = piwikEventNameForHeaderCTAButton
        ? () => {
            if (conversion.onClick) {
              conversion.onClick();
            }
            DataLayer.push({ event: piwikEventNameForHeaderCTAButton });
          }
        : undefined;

      return (
        <IntroText
          key={index}
          isFirstElement={index === 0}
          html={section.text.data.childMarkdownRemark.html}
          ctaLabel={conversion.label}
          link={link}
          showCTA={section.showCTA}
          onCtaClick={onCtaClick}
        />
      );
    }

    if (isTextBlock(section)) {
      return (
        <TextBlock
          key={index}
          isFirstElement={index === 0}
          html={section.Content.data.childMarkdownRemark.html}
        />
      );
    }

    if (isButton(section)) {
      return (
        <StrapiButton
          key={index}
          conversion={{ link: conversion.link, label: conversion.label }}
          {...section}
          {...context}
          sapForm={sapForm}
        />
      );
    }

    if (isImageGroup(section)) {
      return (
        <ImageGroup
          key={index}
          images={section.map((image, idx) => (
            <StrapiImage key={idx} responsiveImage={image} />
          ))}
        />
      );
    }

    if (isVideoWithCaption(section)) {
      const posterImage = section.video.posterImage ? (
        <StrapiImage responsiveImage={section.video.posterImage} />
      ) : null;

      return (
        <ClaimWithContentVideo
          key={index}
          caption={section.caption}
          autoPlay={section.video.autoPlay}
          desktopSources={gatsbyVideoDataFromStrapiVideo(
            section.video.videoDesktop,
          )}
          mobileSources={gatsbyVideoDataFromStrapiVideo(
            section.video.videoMobile,
          )}
          posterImage={posterImage}
        />
      );
    }

    return null;
  });
};
